"use client";

import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence, motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { addDays, format, isBefore, parse } from "date-fns";
import { Icon } from "@iconify-icon/react";
import { Check, Loader2, X } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../dialog.tsx";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./createSlotAlert.tsx";
import { CustomSlotTab } from "./CustomSlotTab.tsx";
import { FillTimeRangeTab } from "./FillTimeRangeTab.tsx";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getDateFnsLocale } from "@/i18n/getDateFnsLocale.ts";
import {
  type CreateSlotFormData,
  createSlotSchema,
  LessonHost,
  type Req,
} from "@/features/calendar/types/create-slot";
import { createSlot } from "@/features/calendar/api/Slots.ts";
import { useCalendarStore } from "@/features/calendar/store/useCalendarStore.tsx";
import { useCalendarModalStore } from "@/features/calendar/store/useCalendarModalStore.tsx";
import { useHostingOptions } from "@/features/calendar/types/hostingOptions.tsx";
import {
  generateDatePreviewSlots,
  generateDateTimeframePreviewSlots,
  getHostingMethodNameAndIcon,
  type PreviewSlot,
} from "@/features/calendar/utils/slot-creation";
import { InformationTooltip } from "@/components/InformationTooltip.tsx";

interface CreateSlotModalProps {
  onSubmit?: (data: CreateSlotFormData) => void;
  children?: ReactNode;
}

export function CreateSlotModal({ children }: CreateSlotModalProps) {
  const { t, i18n } = useTranslation(["forms", "common", "calendar"]);
  const { isOpen, setIsOpen, defaultDate, defaultTime } = useCalendarModalStore();
  const { setToReload, calendarData } = useCalendarStore();
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentOverlapSlot, setCurrentOverlapSlot] = useState<PreviewSlot | null>(null);
  // We no longer need an explicit pause flag – processing awaits user decision via a Promise.
  const confirmationResolver = useRef<((value: boolean) => void) | null>(null);
  const hostingOptions = useHostingOptions();

  // State for two-step process
  const [currentStep, setCurrentStep] = useState<"creation" | "preview">("creation");
  const [previewSlots, setPreviewSlots] = useState<PreviewSlot[]>([]);

  // State for multiple slot creation
  const [createMultiple, setCreateMultiple] = useState(false);
  // State for fill timeframe mode
  const [fillTimeframeMode, setFillTimeframeMode] = useState(false);
  const [timeframeEndTime, setTimeframeEndTime] = useState<string>("10:00");
  // Change the default break minutes value from 15 to 0
  const [breakMinutes, setBreakMinutes] = useState<number | undefined>(0);
  // Update the numberOfSlots state to allow undefined values
  const [numberOfSlots, setNumberOfSlots] = useState<number | undefined>(1);
  const [maxSlots, setMaxSlots] = useState(5);
  const [activeTab, setActiveTab] = useState<"custom" | "range">("custom");
  // State for slot duration in minutes for time range mode
  const [slotDurationMinutes, setSlotDurationMinutes] = useState<number | undefined>(60);

  // synchronise with previous flag so the rest of the logic keeps working
  useEffect(() => {
    setFillTimeframeMode(activeTab === "range");
    if (activeTab === "range") {
      setCreateMultiple(false);
    }
  }, [activeTab]);

  const defaultValues = useMemo(
    () => ({
      start_time: defaultTime ?? "12:00",
      end_time: defaultTime
        ? format(
            addDays(parse(defaultTime, "HH:mm", new Date()), 0).setHours(
              parse(defaultTime, "HH:mm", new Date()).getHours() + 1,
            ),
            "HH:mm",
          )
        : "13:00",
      date: defaultDate ?? format(new Date(), "yyyy-MM-dd"),
      end_date: defaultDate ?? format(new Date(), "yyyy-MM-dd"),
      lesson_host: LessonHost.Ximly,
      overlap_gc: false,
    }),
    [defaultTime, defaultDate],
  );

  const form = useForm<CreateSlotFormData>({
    resolver: zodResolver(createSlotSchema(t)),
    defaultValues,
  });

  useEffect(() => {
    if (isOpen) {
      form.reset(defaultValues);
      setCurrentStep("creation");
      setPreviewSlots([]);
      setBreakMinutes(0);
      setNumberOfSlots(1);
      setCreateMultiple(false);
      setFillTimeframeMode(false);
      setTimeframeEndTime(defaultValues.end_time);
      setCurrentOverlapSlot(null);
      setAlertDialogOpen(false);
    }
  }, [isOpen, form, defaultValues]);

  // Update timeframeEndTime when end_time changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "end_time" && value.end_time) {
        setTimeframeEndTime(value.end_time);
      }
    });

    return () => subscription.unsubscribe();
  }, [form.watch]);

  // Calculate the maximum number of slots that can fit
  useEffect(() => {
    if (form.watch("start_time") && form.watch("end_time")) {
      // Just set a reasonable limit of 10 slots
      setMaxSlots(10);
    }
  }, [form.watch("start_time"), form.watch("end_time")]);

  // Handle proceeding to preview step
  const handleProceedToPreview = async (data: CreateSlotFormData) => {
    const isValid = await form.trigger();

    if (!isValid) {
      return;
    }

    // Additional validation for fill timeframe mode
    if (fillTimeframeMode) {
      if (slotDurationMinutes !== undefined && slotDurationMinutes < 10) {
        toast.error(t("calendar:templates.minSlotDuration"));

        return;
      }
      if (slotDurationMinutes === undefined) {
        toast.error(t("calendar:templates.slotDurationRequired"));

        return;
      }
    }

    // Generate preview slots based on the selected mode
    let slots: PreviewSlot[];

    if (fillTimeframeMode) {
      // Use the new fill timeframe mode
      slots = generateDateTimeframePreviewSlots(
        data,
        timeframeEndTime,
        breakMinutes,
        slotDurationMinutes,
      );
    } else {
      // Use the existing repeat mode
      slots = generateDatePreviewSlots(data, createMultiple, numberOfSlots, breakMinutes);
    }

    // Check if no slots were generated due to validation issues
    if (slots.length === 0) {
      if (fillTimeframeMode) {
        toast.error(t("calendar:templates.noSlotsGenerated"));
      } else {
        toast.error(t("common:error"));
      }

      return;
    }

    // For single slot, create directly without preview
    if (!fillTimeframeMode && slots.length === 1) {
      await handleCreateSlotsDirectly(slots);

      return;
    }

    setPreviewSlots(slots);
    setCurrentStep("preview");
  };

  const handleCreateSlotsDirectly = async (slots: PreviewSlot[]) => {
    setIsSubmitting(true);
    const updatedSlots = [...slots];

    // Process slots one by one
    for (let i = 0; i < updatedSlots.length; i++) {
      const slot = updatedSlots[i];

      try {
        await createIndividualSlot(slot);

        // Update slot status to success
        updatedSlots[i] = { ...slot, status: "success" };
      } catch (error: any) {
        switch (error.response?.data?.code) {
          case "gc_overlap": {
            const shouldOverride = await requestOverlapConfirmation(slot);

            if (shouldOverride) {
              try {
                await createIndividualSlot(slot, true);
                updatedSlots[i] = { ...slot, status: "success" };
              } catch {
                updatedSlots[i] = {
                  ...slot,
                  status: "error",
                  error: t("common:error"),
                };
              }
            } else {
              // user cancelled override
              updatedSlots[i] = {
                ...slot,
                status: "error",
                error: t("forms:createSlotModal.toast.googleCalendarOverlap"),
              };
            }
            break;
          }

          case "database_overlap":
            updatedSlots[i] = {
              ...slot,
              status: "error",
              error: t("forms:createSlotModal.toast.databaseOverlap"),
              errorType: "database_overlap",
            };
            break;

          case "exclusive_lesson_confirmation":
            updatedSlots[i] = {
              ...slot,
              status: "error",
              error: t("forms:createSlotModal.toast.exclusiveLessonConfirmation"),
            };
            break;

          default:
            // Update slot status to error
            updatedSlots[i] = {
              ...slot,
              status: "error",
              error: t("common:error"),
            };
            break;
        }
      }
    }

    // Check if all slots were created successfully
    const allSuccess = updatedSlots.every((slot) => slot.status === "success");

    if (allSuccess) {
      toast.success(t("forms:createSlotModal.toast.slotCreated"), {
        description: t("forms:createSlotModal.toast.slotCreatedDescription"),
      });

      // Only close the dialog automatically if all slots were created successfully
      setTimeout(() => {
        setIsSubmitting(false);
        setIsOpen(false);
        setToReload(true);
      }, 1500);
    } else {
      // For single slot, show the specific error message
      if (updatedSlots.length === 1) {
        const failedSlot = updatedSlots[0];

        toast.error(t("calendar:templates.slotCreationFailed"), {
          description: failedSlot.error || t("forms:createSlotModal.toast.allSlotsFailed"),
        });
      } else {
        toast.error(t("calendar:slotCreationFailed"), {
          description: t("forms:createSlotModal.toast.allSlotsFailed"),
        });
      }
      setIsSubmitting(false);
    }
  };

  // Handle going back to creation step
  const handleBackToCreation = () => {
    setCurrentStep("creation");
  };

  // Format date for API
  const formatDateForApi = (date: string, time: string): string => {
    return `${date} ${time}`;
  };

  // Create individual slot with optional overlap confirmation
  const createIndividualSlot = async (
    slot: PreviewSlot,
    allowOverlap = false,
  ): Promise<boolean> => {
    const startDateTime = parse(`${slot.date} ${slot.start_time}`, "yyyy-MM-dd HH:mm", new Date());
    const endDateTime = parse(`${slot.date} ${slot.end_time}`, "yyyy-MM-dd HH:mm", new Date());

    // If end time is before start time, add a day to the end date
    const adjustedEndDate = isBefore(endDateTime, startDateTime)
      ? format(addDays(parse(slot.date!, "yyyy-MM-dd", new Date()), 1), "yyyy-MM-dd")
      : slot.date;

    // Format data according to API requirements
    const requestData: Req = {
      start_time: formatDateForApi(slot.date!, slot.start_time),
      end_time: formatDateForApi(adjustedEndDate!, slot.end_time),
      lesson_host: slot.lesson_host as LessonHost,
      overlap_gc: allowOverlap ? 1 : slot.overlap_gc ? 1 : 0,
    };

    // Add communication_details if lesson_host is OtherWay
    if (slot.lesson_host === LessonHost.OtherWay && slot.communication_details) {
      requestData.communication_details = slot.communication_details;
    }

    try {
      await createSlot(requestData);

      return true;
    } catch (error: any) {
      throw error;
    }
  };

  /**
   * Ask the user whether to override Google-Calendar overlap for the given slot.
   * Resolves to true when the user clicks "Continue", false when "Cancel".
   */
  const requestOverlapConfirmation = async (slot: PreviewSlot): Promise<boolean> => {
    setCurrentOverlapSlot(slot);
    setAlertDialogOpen(true);

    // Create a promise that will be resolved by dialog buttons
    return new Promise<boolean>((resolve) => {
      confirmationResolver.current = (value: boolean) => {
        resolve(value);
      };
    });
  };

  // Handle creating slots from preview
  const handleSubmit = async () => {
    setIsSubmitting(true);
    // processing is sequential but awaits user confirmation when needed

    // Create a copy of preview slots to update their status
    const updatedSlots = [...previewSlots];

    // Process slots one by one
    for (let i = 0; i < updatedSlots.length; i++) {
      const slot = updatedSlots[i];

      try {
        await createIndividualSlot(slot);

        // Update slot status to success
        updatedSlots[i] = { ...slot, status: "success" };
        setPreviewSlots([...updatedSlots]);
      } catch (error: any) {
        switch (error.response?.data?.code) {
          case "gc_overlap": {
            const shouldOverride = await requestOverlapConfirmation(slot);

            if (shouldOverride) {
              try {
                await createIndividualSlot(slot, true);
                updatedSlots[i] = { ...slot, status: "success" };
              } catch {
                updatedSlots[i] = {
                  ...slot,
                  status: "error",
                  error: t("common:error"),
                };
              }
            } else {
              // user cancelled override
              updatedSlots[i] = {
                ...slot,
                status: "error",
                error: t("forms:createSlotModal.toast.googleCalendarOverlap"),
              };
            }

            setPreviewSlots([...updatedSlots]);
            break;
          }

          case "database_overlap":
            updatedSlots[i] = {
              ...slot,
              status: "error",
              error: t("forms:createSlotModal.toast.databaseOverlap"),
              errorType: "database_overlap",
            };
            setPreviewSlots([...updatedSlots]);
            break;

          case "exclusive_lesson_confirmation":
            updatedSlots[i] = {
              ...slot,
              status: "error",
              error: t("forms:createSlotModal.toast.exclusiveLessonConfirmation"),
            };
            setPreviewSlots([...updatedSlots]);
            break;

          default:
            // Update slot status to error
            updatedSlots[i] = {
              ...slot,
              status: "error",
              error: t("common:error"),
            };
            setPreviewSlots([...updatedSlots]);
            break;
        }
      }
    }

    // Check if all slots were created successfully
    const allSuccess = updatedSlots.every((slot) => slot.status === "success");
    const successCount = updatedSlots.filter((slot) => slot.status === "success").length;

    if (allSuccess) {
      toast.success(
        updatedSlots.length > 1
          ? t("forms:createSlotModal.toast.multipleSlotsCreated", { count: updatedSlots.length })
          : t("forms:createSlotModal.toast.slotCreated"),
        {
          description:
            updatedSlots.length > 1
              ? t("forms:createSlotModal.toast.multipleSlotsCreatedDescription", {
                  count: updatedSlots.length,
                })
              : t("forms:createSlotModal.toast.slotCreatedDescription"),
        },
      );

      // Only close the dialog automatically if all slots were created successfully
      setTimeout(() => {
        setIsSubmitting(false);
        setIsOpen(false);
        setToReload(true);
      }, 1500);
    } else if (successCount > 0) {
      toast.success(t("forms:createSlotModal.toast.someSlotCreated", { count: successCount }), {
        description: t("forms:createSlotModal.toast.someSlotCreatedDescription"),
      });
      setIsSubmitting(false);
      setToReload(true);
    } else {
      toast.error(t("calendar:slotCreationFailed"), {
        description: t("forms:createSlotModal.toast.allSlotsFailed"),
      });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {children && <DialogTrigger>{children}</DialogTrigger>}
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            setIsOpen(false);
          }
        }}
      >
        <DialogContent
          aria-describedby={undefined}
          className="bg-background absolute! left-0 flex h-dvh max-w-full flex-col gap-0 rounded-e-none! border-none p-0 focus-visible:ring-0 focus-visible:ring-offset-0 md:left-[35%] md:max-w-[65%] xl:left-[60%] xl:max-w-[40vw]"
        >
          <motion.div
            animate={{ x: 0, opacity: 1 }}
            className="flex h-full flex-col"
            exit={{ x: 100, opacity: 0 }}
            initial={{ x: 100, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <DialogHeader className="border-border border-b bg-transparent px-6 py-4">
              <DialogTitle className="text-accent-foreground text-xl font-semibold">
                {currentStep === "creation"
                  ? t("calendar:templates.addNewSlot")
                  : t("calendar:templates.previewSlots")}
              </DialogTitle>
            </DialogHeader>

            <AnimatePresence mode="wait">
              {currentStep === "creation" ? (
                <motion.div
                  key="creation-step"
                  animate={{ opacity: 1 }}
                  className="flex flex-grow flex-col"
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Form {...form}>
                    <div className="flex h-full flex-col">
                      <ScrollArea className="h-[calc(100dvh_-_140px)]">
                        {/* Hoisted tab switcher */}
                        <div className="mx-6 mt-4">
                          <Card className="bg-muted/20 relative rounded-2xl border-0 p-2 shadow-2xl backdrop-blur-xl">
                            <div className={"absolute inset-0 overflow-hidden rounded-2xl"}>
                              <div className="absolute -top-10 -right-10 h-20 w-20 rounded-full bg-gradient-to-br from-blue-500/20 to-purple-600/20 blur-2xl" />
                              <div className="absolute -bottom-8 -left-8 h-16 w-16 rounded-full bg-gradient-to-tr from-green-500/15 to-emerald-600/15 blur-xl" />
                            </div>
                            <div className="relative z-10 grid grid-cols-2 gap-2">
                              <button
                                className={`relative overflow-hidden rounded-xl p-4 transition-all duration-200 ${
                                  activeTab === "custom"
                                    ? "bg-background shadow-lg"
                                    : "hover:bg-background/50"
                                }`}
                                onClick={() => setActiveTab("custom")}
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`flex size-10 items-center justify-center rounded-lg transition-colors ${
                                      activeTab === "custom" ? "bg-muted" : "bg-muted/50"
                                    }`}
                                  >
                                    <Icon
                                      className={
                                        activeTab === "custom"
                                          ? "text-accent-foreground"
                                          : "text-muted-foreground"
                                      }
                                      height={"1.25rem"}
                                      icon="tabler:calendar-plus"
                                      width={"1.25rem"}
                                    />
                                  </div>
                                  <div className="text-left">
                                    <div
                                      className={`text-sm font-medium transition-colors ${
                                        activeTab === "custom"
                                          ? "text-accent-foreground"
                                          : "text-muted-foreground"
                                      }`}
                                    >
                                      {t("calendar:templates.singleSlot")}
                                    </div>
                                    <div className="text-muted-foreground mt-0.5 text-xs">
                                      {t("calendar:templates.createSingleSlot")}
                                    </div>
                                  </div>
                                </div>
                              </button>
                              <button
                                className={`relative overflow-hidden rounded-xl p-4 transition-all duration-200 ${
                                  activeTab === "range"
                                    ? "bg-background shadow-lg"
                                    : "hover:bg-background/50"
                                }`}
                                onClick={() => setActiveTab("range")}
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`flex size-10 items-center justify-center rounded-lg transition-colors ${
                                      activeTab === "range" ? "bg-muted" : "bg-muted/50"
                                    }`}
                                  >
                                    <Icon
                                      className={
                                        activeTab === "range"
                                          ? "text-accent-foreground"
                                          : "text-muted-foreground"
                                      }
                                      height={"1.25rem"}
                                      icon="tabler:calendar-time"
                                      width={"1.25rem"}
                                    />
                                  </div>
                                  <div className="text-left">
                                    <div
                                      className={`text-sm font-medium transition-colors ${
                                        activeTab === "range"
                                          ? "text-accent-foreground"
                                          : "text-muted-foreground"
                                      }`}
                                    >
                                      {t("calendar:templates.fillTimeframe")}
                                    </div>
                                    <div className="text-muted-foreground mt-0.5 text-xs">
                                      {t("calendar:templates.fillTimeframeDescription")}
                                    </div>
                                  </div>
                                </div>
                              </button>
                            </div>
                          </Card>
                        </div>

                        {/* Animated content wrapper */}
                        <AnimatePresence mode="wait">
                          <motion.div
                            key={activeTab}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            initial={{ opacity: 0, y: 15 }}
                            transition={{ duration: 0.2, ease: "easeInOut" }}
                          >
                            {activeTab === "custom" ? (
                              <CustomSlotTab
                                breakMinutes={breakMinutes}
                                calendarData={calendarData}
                                createMultiple={createMultiple}
                                form={form}
                                hostingOptions={hostingOptions}
                                i18n={i18n}
                                maxSlots={maxSlots}
                                numberOfSlots={numberOfSlots}
                                setBreakMinutes={setBreakMinutes}
                                setCreateMultiple={setCreateMultiple}
                                setNumberOfSlots={setNumberOfSlots}
                                t={t}
                              />
                            ) : (
                              <FillTimeRangeTab
                                breakMinutes={breakMinutes}
                                calendarData={calendarData}
                                form={form}
                                hostingOptions={hostingOptions}
                                i18n={i18n}
                                setBreakMinutes={setBreakMinutes}
                                setSlotDurationMinutes={setSlotDurationMinutes}
                                setTimeframeEndTime={setTimeframeEndTime}
                                slotDurationMinutes={slotDurationMinutes}
                                t={t}
                                timeframeEndTime={timeframeEndTime}
                              />
                            )}
                          </motion.div>
                        </AnimatePresence>
                      </ScrollArea>

                      {/* Action buttons */}
                      <AnimatePresence mode="wait">
                        {activeTab === "custom" ? (
                          <motion.div
                            key="custom-actions"
                            animate={{ opacity: 1, y: 0 }}
                            className="border-border bg-background p-6 pt-0"
                            initial={{ opacity: 0, y: 20 }}
                            transition={{ delay: 0.3 }}
                          >
                            <div className="mt-6 flex justify-end">
                              <Button
                                disabled={isSubmitting}
                                variant={"foreground"}
                                onClick={form.handleSubmit(handleProceedToPreview)}
                              >
                                <div className="flex items-center gap-1.5">
                                  <Icon height="18" icon="tabler:eye" width="18" />
                                  <span>
                                    {!fillTimeframeMode && numberOfSlots === 1
                                      ? t("calendar:templates.createSlot")
                                      : t("calendar:templates.previewSlots")}
                                  </span>
                                </div>
                              </Button>
                            </div>
                          </motion.div>
                        ) : (
                          <motion.div
                            key="range-actions"
                            animate={{ opacity: 1, y: 0 }}
                            className="border-border bg-background p-6 pt-0"
                            initial={{ opacity: 0, y: 20 }}
                            transition={{ delay: 0.3 }}
                          >
                            <div className="mt-6 flex justify-end">
                              <Button
                                disabled={isSubmitting}
                                variant={"foreground"}
                                onClick={form.handleSubmit(handleProceedToPreview)}
                              >
                                <div className="flex items-center gap-1.5">
                                  <Icon height="18" icon="tabler:eye" width="18" />
                                  <span>
                                    {!fillTimeframeMode && numberOfSlots === 1
                                      ? t("calendar:templates.createSlot")
                                      : t("calendar:templates.previewSlots")}
                                  </span>
                                </div>
                              </Button>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </Form>
                </motion.div>
              ) : (
                <motion.div
                  key="preview-step"
                  animate={{ opacity: 1 }}
                  className="flex flex-grow flex-col"
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ScrollArea className="h-[calc(100dvh_-_130px)]">
                    <div className="space-y-6 p-6">
                      <motion.div
                        animate={{ opacity: 1, y: 0 }}
                        initial={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="space-y-4">
                          {previewSlots.map((slot, index) => {
                            const { name, icon } = getHostingMethodNameAndIcon(
                              slot.lesson_host,
                              hostingOptions,
                            );

                            return (
                              <motion.div
                                key={`preview-slot-${slot.id}`}
                                animate={{ opacity: 1, y: 0 }}
                                initial={{ opacity: 0, y: 20 }}
                                transition={{ duration: 0.3, delay: index * 0.1 }}
                              >
                                <Card className="bg-background/40 relative overflow-hidden rounded-2xl border-0 shadow-xl backdrop-blur-xl">
                                  <div className="absolute -top-12 -right-12 h-24 w-24 rounded-full bg-gradient-to-br from-emerald-500/15 to-teal-600/15 blur-2xl" />
                                  <CardContent className="p-4">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-3">
                                        <div className="bg-muted flex size-10 items-center justify-center rounded-lg">
                                          <Icon
                                            className="text-accent-foreground"
                                            height={"1.25rem"}
                                            icon="tabler:calendar-stats"
                                            width={"1.25rem"}
                                          />
                                        </div>
                                        <div>
                                          <div className="flex items-center gap-2">
                                            <h3 className="text-accent-foreground text-base font-medium">
                                              {`${slot.start_time} - ${slot.end_time}`}
                                            </h3>
                                            <Badge className="bg-sidebar/30 text-accent-foreground">
                                              {format(new Date(slot.date!), "PPP", {
                                                locale: getDateFnsLocale(i18n.language),
                                              })}
                                            </Badge>
                                          </div>
                                          <div className="mt-1 flex items-center gap-2">
                                            <p className="text-muted-foreground flex items-center gap-1 text-sm">
                                              <span
                                                className={"flex h-4 items-center gap-1 text-sm"}
                                              >
                                                <span className={"size-4"}>{icon}</span>
                                                {name}
                                              </span>
                                            </p>
                                          </div>
                                        </div>
                                      </div>

                                      <div className="flex items-center">
                                        {isSubmitting ? (
                                          <>
                                            {slot.status === "pending" && (
                                              <div className="bg-muted/70 flex h-8 w-8 items-center justify-center rounded-full">
                                                <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
                                              </div>
                                            )}
                                            {slot.status === "success" && (
                                              <motion.div
                                                animate={{ scale: 1, opacity: 1 }}
                                                className="flex h-8 w-8 items-center justify-center rounded-full bg-green-500/20"
                                                initial={{ scale: 0.5, opacity: 0 }}
                                              >
                                                <Check className="h-5 w-5 text-green-500" />
                                              </motion.div>
                                            )}
                                            {slot.status === "error" && (
                                              <motion.div
                                                animate={{ scale: 1, opacity: 1 }}
                                                className="flex h-8 w-8 items-center justify-center rounded-full bg-red-500/20"
                                                initial={{ scale: 0.5, opacity: 0 }}
                                                title={slot.error}
                                              >
                                                <X className="h-5 w-5 text-red-500" />
                                              </motion.div>
                                            )}
                                          </>
                                        ) : (
                                          <div className="bg-muted/70 flex h-8 w-8 items-center justify-center rounded-full">
                                            <Icon
                                              className="text-muted-foreground"
                                              height={"1.25rem"}
                                              icon="tabler:clock"
                                              width={"1.25rem"}
                                            />
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </CardContent>
                                  {slot.status === "error" && slot.error && (
                                    <motion.div
                                      key={`error-message-${slot.id}`}
                                      animate={{ opacity: 1, height: "auto" }}
                                      className="mt-2 px-4 pb-4"
                                      initial={{ opacity: 0, height: 0 }}
                                      transition={{ duration: 0.3 }}
                                    >
                                      <div className="rounded-md border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-500">
                                        <div className="flex items-start">
                                          <Icon
                                            className="mt-0.5 mr-2 flex-shrink-0"
                                            height={"1rem"}
                                            icon="tabler:alert-circle"
                                            width={"1rem"}
                                          />
                                          <div className="flex w-full items-center justify-between">
                                            <span>{slot.error}</span>
                                            {slot.errorType === "database_overlap" && (
                                              <InformationTooltip
                                                description={t(
                                                  "forms:createSlotModal.errors.databaseOverlapTooltip",
                                                )}
                                              />
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </motion.div>
                                  )}
                                </Card>
                              </motion.div>
                            );
                          })}
                        </div>
                      </motion.div>
                    </div>
                  </ScrollArea>

                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    className="border-border bg-background flex h-16 items-center justify-between border-t px-6 py-1"
                    initial={{ opacity: 0, y: 20 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Button
                      disabled={isSubmitting}
                      variant="outline"
                      onClick={handleBackToCreation}
                    >
                      <div className="flex items-center gap-1.5">
                        <Icon height="18" icon="tabler:arrow-left" width="18" />
                        <span>{t("common:back")}</span>
                      </div>
                    </Button>

                    <Button
                      disabled={
                        isSubmitting || previewSlots.some((slot) => slot.status === "error")
                      }
                      variant={"foreground"}
                      onClick={() => handleSubmit()}
                    >
                      {isSubmitting ? (
                        <div className="flex items-center">
                          <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                          {t("common:creating")}
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <Icon
                            height="18"
                            icon={createMultiple ? "tabler:calendar-repeat" : "tabler:plus"}
                            width="18"
                          />
                          <span>{t("calendar:templates.confirmSlots")}</span>
                        </div>
                      )}
                    </Button>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>

          <AlertDialog
            open={alertDialogOpen}
            onOpenChange={(open) => {
              if (!open) {
                setAlertDialogOpen(false);
                setCurrentOverlapSlot(null);
              }
            }}
          >
            <AlertDialogContent className={"border-destructive max-w-lg"}>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("forms:createSlotModal.alertDialog.title")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("forms:createSlotModal.alertDialog.description")}
                </AlertDialogDescription>
              </AlertDialogHeader>

              {currentOverlapSlot && (
                <div className="my-4">
                  <h4 className="mb-2 text-sm font-medium">Slot Details:</h4>
                  <Card className="bg-background/40 relative overflow-hidden rounded-xl border">
                    <CardContent className="p-3">
                      <div className="flex items-center gap-3">
                        <div className="bg-muted flex size-8 items-center justify-center rounded-lg">
                          <Icon
                            className="text-accent-foreground"
                            height={"1rem"}
                            icon="tabler:calendar-stats"
                            width={"1rem"}
                          />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-accent-foreground text-sm font-medium">
                              {`${currentOverlapSlot.start_time} - ${currentOverlapSlot.end_time}`}
                            </span>
                            <Badge className="bg-sidebar/30 text-accent-foreground text-xs">
                              {format(new Date(currentOverlapSlot.date!), "PPP", {
                                locale: getDateFnsLocale(i18n.language),
                              })}
                            </Badge>
                          </div>
                          <div className="mt-1 flex items-center gap-2">
                            <p className="text-muted-foreground flex items-center gap-1 text-xs">
                              <span className={"flex h-3 items-center gap-1 text-xs"}>
                                <span className={"size-3"}>
                                  {
                                    getHostingMethodNameAndIcon(
                                      currentOverlapSlot.lesson_host,
                                      hostingOptions,
                                    ).icon
                                  }
                                </span>
                                {
                                  getHostingMethodNameAndIcon(
                                    currentOverlapSlot.lesson_host,
                                    hostingOptions,
                                  ).name
                                }
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              <AlertDialogFooter>
                <AlertDialogCancel
                  onClick={() => {
                    // Inform awaiting promise that the user cancelled
                    confirmationResolver.current?.(false);

                    // UI housekeeping
                    setAlertDialogOpen(false);
                    setCurrentOverlapSlot(null);
                  }}
                >
                  {t("forms:common.cancel")}
                </AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive/50 hover:destructive/30"
                  onClick={() => {
                    // user chose to override – resolve promise with true
                    confirmationResolver.current?.(true);
                    setAlertDialogOpen(false);
                    setCurrentOverlapSlot(null);
                  }}
                >
                  {t("forms:common.continue")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </DialogContent>
      </Dialog>
    </>
  );
}
